import React from 'react';
import {
  MdLockOutline,
  MdPlayCircleOutline,
  MdOutlineAddShoppingCart,
} from 'react-icons/md';

import { Course as CourseInterface } from '../../../models/CourseModels';

import Button from '../Button';
import ButtonBlocked from '../ButtonBlocked';

/**
 * Parcelamento exibido no card.
 *
 * Fórmula copiada de CourseCardV2 sem alteração — inclusive o fator 1.0459
 * aplicado acima de 6 parcelas e o padrão de 12x quando max_installments não
 * vem. É regra de negócio de ~5 anos: se o número mudar, o defeito não tem
 * dono. Aqui ela existe uma vez, em vez de duas (V2 e Mobile).
 */
export function formatInstallments(course: CourseInterface): string {
  if (course.subscription) {
    return `${Number(course.subscription_installments).toFixed(0)}x de R$${Number(
      course.price,
    ).toFixed(2)}`;
  }

  const installments = course.max_installments > 0 ? course.max_installments : 12;
  const factor =
    course.max_installments && course.max_installments <= 6 ? 1 : 1.0459;

  return `${installments}x de R$${Number((course.price * factor) / installments).toFixed(2)}`;
}

interface CourseCtaProps {
  course: CourseInterface;
  isInCart: boolean;
  onOpenCourse: () => void;
  onAddToCart: () => void;
}

/**
 * A árvore de CTA do card.
 *
 * Copiada de CourseCardV2 (o bloco de ContentCardWrap com os botões) sem
 * alteração de condição, de ordem ou de rótulo. São sete ramos, e cada um
 * decide se o aluno compra, entra no curso ou é mandado para /finance:
 *
 *   comingsoon === 'true'                        -> EM BREVE
 *   !is_owner + já no carrinho                   -> Item já adicionado
 *   !is_owner                                    -> Adicionar ao carrinho
 *   is_owner + blocked                           -> Acesso Bloqueado
 *   is_owner + status_payment !== 'PAID'         -> Pagamento Pendente
 *   is_owner + clicksign_is_signed === 'false'   -> Contrato Pendente
 *   is_owner                                     -> Ir para o curso
 *
 * A cadeia de if abaixo tem exatamente a mesma precedência da cadeia de
 * ternários original. Mudar a ordem muda quem consegue entrar no curso.
 */
const CourseCta: React.FC<CourseCtaProps> = ({
  course,
  isInCart,
  onOpenCourse,
  onAddToCart,
}) => {
  if (course.comingsoon === 'true') {
    return <p className="cta-note">EM BREVE</p>;
  }

  if (!course.is_owner) {
    return isInCart ? (
      <Button disabled>
        <MdOutlineAddShoppingCart aria-hidden="true" focusable="false" />
        Item já adicionado
      </Button>
    ) : (
      <Button onClick={onAddToCart}>
        <MdOutlineAddShoppingCart aria-hidden="true" focusable="false" />
        Adicionar ao carrinho
      </Button>
    );
  }

  if (course.blocked) {
    return (
      <ButtonBlocked onClick={onOpenCourse}>
        <MdLockOutline aria-hidden="true" focusable="false" />
        Acesso Bloqueado
      </ButtonBlocked>
    );
  }

  if (course.status_payment && course.status_payment !== 'PAID') {
    return (
      <ButtonBlocked onClick={onOpenCourse}>
        <MdLockOutline aria-hidden="true" focusable="false" />
        Pagamento Pendente
      </ButtonBlocked>
    );
  }

  if (course.clicksign_is_signed && course.clicksign_is_signed === 'false') {
    return (
      <ButtonBlocked onClick={onOpenCourse}>
        <MdLockOutline aria-hidden="true" focusable="false" />
        Contrato Pendente
      </ButtonBlocked>
    );
  }

  return (
    <Button onClick={onOpenCourse}>
      <MdPlayCircleOutline aria-hidden="true" focusable="false" />
      Ir para o curso
    </Button>
  );
};

export default CourseCta;
